import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Pencil } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link } from 'wouter';
import { BranchPicker } from '../components/ui/BranchPicker';
import { CompleteTaskModal } from '../components/ui/CompleteTaskModal';
import { Select } from '../components/ui/Select';
import { StaffPicker } from '../components/ui/StaffPicker';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { useBranches, useStaffList } from '../lib/options';
import { apiErrorMessage, useToast } from '../lib/ToastContext';
import { assetUrl } from '../lib/upload';
import { Loader } from '../components/ui/Spinner';

type TaskDetail = {
  id: string; taskNumber: string; title: string; description: string | null; assignedTo: string | null; assignedBranchId: string | null;
  assignedBy: string; dueAt: string; status: 'open' | 'completed' | 'overdue'; completedAt: string | null; completionNote: string | null; audioUrl: string | null;
};

const MANAGER_ROLES = ['branch_head', 'region_head', 'general_head', 'hr', 'admin'] as const;

/** `datetime-local` inputs read/write local wall-clock time, not UTC — converting the task's
 * ISO due date through Date's local getters (not toISOString) is what keeps the value shown in
 * the edit form matching what "Due:" displays above it. */
function toLocalInputValue(iso: string) {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function TaskDetailPage({ id }: { id: string }) {
  const { user } = useAuth();
  const client = useQueryClient();
  const { success, error: toastError } = useToast();
  const staff = useStaffList();
  const branches = useBranches();
  const task = useQuery({ queryKey: ['task', id], queryFn: async () => (await api.get<TaskDetail>(`/api/tasks/${id}`)).data });
  const [completing, setCompleting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editTarget, setEditTarget] = useState<'user' | 'branch'>('user');
  const [editAssignedTo, setEditAssignedTo] = useState('');
  const [editAssignedBranchId, setEditAssignedBranchId] = useState('');
  const [editDueAt, setEditDueAt] = useState('');

  const complete = useMutation({
    mutationFn: (note: string) => api.post(`/api/tasks/${id}/complete`, { note }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['task', id] });
      void client.invalidateQueries({ queryKey: ['tasks'] });
      success('Task marked complete');
      setCompleting(false);
    },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not complete task')),
  });

  const update = useMutation({
    mutationFn: () => api.patch(`/api/tasks/${id}`, {
      title: editTitle,
      description: editDescription || null,
      dueAt: new Date(editDueAt).toISOString(),
      ...(editTarget === 'user' ? { assignedTo: editAssignedTo, assignedBranchId: null } : { assignedBranchId: editAssignedBranchId, assignedTo: null }),
    }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['task', id] });
      void client.invalidateQueries({ queryKey: ['tasks'] });
      success('Task updated');
      setEditing(false);
    },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not update task')),
  });

  if (task.isLoading) return <Loader />;
  if (!task.data) return <div className="card text-center text-muted">Task not found.</div>;

  const t = task.data;
  const currentUserId = user?.id ?? user?.sub;
  const isManager = user?.roles.some((r) => (MANAGER_ROLES as readonly string[]).includes(r)) ?? false;
  const canComplete = t.status !== 'completed' && (t.assignedTo === currentUserId || isManager);
  const canEdit = isManager && t.status !== 'completed';
  const isOverdue = t.status === 'open' && new Date(t.dueAt) < new Date();
  const assigneeName = t.assignedTo ? staff.data?.find((s) => s.id === t.assignedTo)?.name ?? 'Staff' : null;
  const branchName = t.assignedBranchId ? branches.data?.find((b) => b.id === t.assignedBranchId)?.name ?? 'Branch' : null;
  const assignedByName = staff.data?.find((s) => s.id === t.assignedBy)?.name ?? (t.assignedBy === currentUserId ? 'You' : 'Manager');

  const openEdit = () => {
    setEditTitle(t.title);
    setEditDescription(t.description ?? '');
    setEditTarget(t.assignedBranchId ? 'branch' : 'user');
    setEditAssignedTo(t.assignedTo ?? '');
    setEditAssignedBranchId(t.assignedBranchId ?? '');
    setEditDueAt(toLocalInputValue(t.dueAt));
    setEditing(true);
  };

  return (
    <>
      <Link href="/tasks" className="mb-4 inline-block text-sm font-medium text-brand-700">← Back to tasks</Link>
      <div className="card">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="font-mono text-xs font-semibold tracking-wide text-muted">{t.taskNumber}</p>
            {!editing && <h1 className={`text-2xl font-bold ${t.status === 'completed' ? 'text-muted line-through' : ''}`}>{t.title}</h1>}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <span className={`badge ${isOverdue ? 'bg-red-50 text-red-700' : t.status === 'completed' ? 'bg-brand-100 text-brand-700' : ''}`}>
              {isOverdue && <AlertTriangle size={12} className="mr-1" />}
              {isOverdue ? 'overdue' : t.status}
            </span>
            {canEdit && !editing && <button type="button" className="btn-secondary" onClick={openEdit}><Pencil size={16} /> Edit</button>}
          </div>
        </div>

        {editing ? (
          <form className="mt-4 grid gap-4 sm:grid-cols-2" onSubmit={(e: FormEvent) => { e.preventDefault(); update.mutate(); }}>
            <label className="label sm:col-span-2">Title<input className="field mt-1" required value={editTitle} onChange={(e) => setEditTitle(e.target.value)} /></label>
            <label className="label sm:col-span-2">Description<textarea className="field mt-1 min-h-20 py-2" value={editDescription} onChange={(e) => setEditDescription(e.target.value)} /></label>
            <label className="label">Assign to<Select className="mt-1" value={editTarget} onChange={(e) => setEditTarget(e.target.value as 'user' | 'branch')}><option value="user">A staff member</option><option value="branch">Everyone at a branch</option></Select></label>
            {editTarget === 'user'
              ? <label className="label">Staff member<StaffPicker required value={editAssignedTo} onChange={setEditAssignedTo} /></label>
              : <label className="label">Branch<BranchPicker required value={editAssignedBranchId} onChange={setEditAssignedBranchId} /></label>}
            <label className="label sm:col-span-2">Deadline<input type="datetime-local" step={3600} className="field mt-1" required value={editDueAt} onChange={(e) => setEditDueAt(e.target.value)} /></label>
            <div className="flex items-end gap-2 sm:col-span-2">
              <button className="btn-primary" disabled={update.isPending}>Save changes</button>
              <button type="button" className="btn-secondary" onClick={() => setEditing(false)}>Cancel</button>
            </div>
          </form>
        ) : (
          <>
            {t.description && <p className="mt-3 whitespace-pre-wrap text-muted">{t.description}</p>}

            <div className="mt-4 grid gap-2 text-sm text-muted sm:grid-cols-2">
              <p>Assigned to: <span className="font-medium text-ink">{assigneeName ?? `Everyone at ${branchName ?? 'branch'}`}</span></p>
              <p>Assigned by: <span className="font-medium text-ink">{assignedByName}</span></p>
              <p>Due: <span className="font-medium text-ink">{new Date(t.dueAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</span></p>
              {t.completedAt && <p>Completed: <span className="font-medium text-ink">{new Date(t.completedAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</span></p>}
            </div>
          </>
        )}

        {t.completionNote && (
          <div className="mt-4 rounded-xl bg-canvas p-3">
            <p className="label mb-1">Completion note</p>
            <p className="whitespace-pre-wrap text-sm text-ink">{t.completionNote}</p>
          </div>
        )}

        {t.audioUrl && (
          <div className="mt-4">
            <p className="label mb-1">Voice note</p>
            <audio controls className="w-full" src={assetUrl(t.audioUrl)} />
          </div>
        )}

        {canComplete && !editing && (
          <button className="btn-primary mt-5 w-full sm:w-auto" onClick={() => setCompleting(true)}>
            <CheckCircle2 size={18} /> Mark complete
          </button>
        )}
      </div>
      {completing && (
        <CompleteTaskModal
          taskTitle={t.title}
          isPending={complete.isPending}
          onConfirm={(note) => complete.mutate(note)}
          onClose={() => setCompleting(false)}
        />
      )}
    </>
  );
}
